import {Injectable} from "@angular/core";
import {environment} from "../../environments/environment";
import {HttpClient, HttpErrorResponse, HttpParams} from "@angular/common/http";
import {catchError, map, Observable, throwError} from "rxjs";
import {RemitoVehicle, Vin} from "../models/RemitoVehicle";
import {ViewVehicle} from "../models/ViewVehicle";
import {ViewVehicleSearch} from "../models/ViewVehicleSearch";
import {VehRemPrinted} from "../models/VehRemPrinted";
import {RemitoVehicleCreationDetails} from "../models/RemitoVehicleCreationDetails";
import {FabFilterDescriptor} from "@fabric/components/table-filter/data-query";
import {PagedResponse} from "../models/PagedResponse";

@Injectable({
  providedIn: 'root'
})
export class RemitoVehicleService {
  private readonly vehicleRemitosUrl = environment.vehicleRemitosUrl;
  private readonly vinVehicleUrl = environment.vinVehicleUrl;
  private readonly excelUrl = environment.excelUrl;
  vehRemPrintedParams: HttpParams;
  viewVehicleParams : HttpParams;

  constructor(private readonly httpClient: HttpClient) { }

  getDetails(type: string): Observable<RemitoVehicleCreationDetails> {
    return this.httpClient.get<RemitoVehicleCreationDetails>(this.vehicleRemitosUrl + "/creationLov/lists/" + type);
  }

  addVehicleRemito(remito: RemitoVehicle): Observable<string> {
    return this.httpClient.post<string>(this.vehicleRemitosUrl, remito).pipe(
      catchError((err) => {
        console.log("ERROR! Add remito failed: ", err)
        return throwError(err);
      })
    );
  }

  getVinDetails(vinLetras: String, vinNumeros: String): Observable<Vin> {
    return this.httpClient.get<Vin>(this.vinVehicleUrl + "/" + vinLetras + "/" + vinNumeros).pipe(
      catchError((err) => {
        console.log("Vin Details Error", err)
        return throwError(err);
      })
    );
  }

  getRemitoById(ptoEmision: string, nro: string): Observable<RemitoVehicle> {
    return this.httpClient.get<RemitoVehicle>(this.vehicleRemitosUrl + "/" + ptoEmision + "/" + nro).pipe(
      map((response) => {
        return RemitoVehicle.transformRemitoVehicle(response);
      }),
      catchError((err: any) => {
        console.log("Retrieve All Remitos Error", err)
        return throwError(err);
      })
    );
  }

  getAllRemitos(): Observable<ViewVehicle[]> {
    return this.httpClient.get<ViewVehicle[]>(this.vehicleRemitosUrl).pipe(
      map((response) => {
        return response.map((remito) => {
          return ViewVehicle.transformViewVehicle(remito);
        })
      }),
      catchError((err: any) => {
        console.log("Retrieve All Remitos Error", err)
        return throwError(err);
      })
    );
  }

  public getPage(
    page: number,
    pageSize: number,
    sortFields: string[],
    sortDirection: 'asc' | 'desc' | '',
    filters: FabFilterDescriptor[],
    searchCriteria: ViewVehicleSearch
  ): Observable<PagedResponse<ViewVehicle>> {
    searchCriteria.page = page;
    searchCriteria.pageSize = pageSize;
    searchCriteria.sortFields = sortFields;
    searchCriteria.sortDirection = sortDirection;
    searchCriteria.filters = filters;

    return this.httpClient.post<PagedResponse<ViewVehicle>>(this.vehicleRemitosUrl +"/all_remitos", searchCriteria)
      .pipe(
        map((a: PagedResponse<ViewVehicle>) => {
          a.data = a.data.map(e => ViewVehicle.transformViewVehicle(e));
          return a;
        }),
        catchError((err) => {
          console.log("Retrieve All Remitos Error", err)
          return throwError(err);
        })
      );
  }

  getRemitosBySearchCriteria(searchCriteria: ViewVehicleSearch): Observable<ViewVehicle[]> {
    console.log(searchCriteria);
    return this.httpClient.post<ViewVehicle[]>(this.vehicleRemitosUrl + "/all_remitos", searchCriteria).pipe(
      map((response) => {
        return response.map((remito) => {
          return ViewVehicle.transformViewVehicle(remito);
        })
      }),
      catchError((err) => {
        console.log("Retrieve All Remitos Error", err)
        return throwError(err);
      })
    );
  }


  downloadExcel(searchCriteria: ViewVehicleSearch): Observable<Blob>  {
    this.getViewVehicleParams(searchCriteria);
    return this.httpClient.get(this.excelUrl + '/viewVehicleRemitos', {params: this.viewVehicleParams, responseType: 'blob'}).pipe(catchError(this.parseErrorBlob));
  }

  getViewVehicleParams(searchCriteria: ViewVehicleSearch){

    this.viewVehicleParams = new HttpParams();

    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('vinLetras', searchCriteria.vinLetras);
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('vinNumeros', searchCriteria.vinNumeros);
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('ptoEmision', searchCriteria.ptoEmision);
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('nroMax', searchCriteria.nroMax);
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('nroMin', searchCriteria.nroMin);
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('nombreDestino', searchCriteria.destinatario);
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('codTransp', searchCriteria.transporte)
    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('origen', searchCriteria.origen);

    if(searchCriteria.fecGenDesde){
      this.viewVehicleParams = this.viewVehicleParams.append('fecGenDesde', searchCriteria.fecGenDesde?.toISOString());
    }else{
      this.viewVehicleParams = this.viewVehicleParams.append('fecGenDesde', "");
    }

    if(searchCriteria.fecGenHasta){
      this.viewVehicleParams = this.viewVehicleParams.append('fecGenHasta', searchCriteria.fecGenHasta?.toISOString());
    }else{
      this.viewVehicleParams = this.viewVehicleParams.append('fecGenHasta', "");
    }

    if(searchCriteria.despachoDesde){
      this.viewVehicleParams = this.viewVehicleParams.append('despachoDesde', searchCriteria.despachoDesde?.toISOString());
    }else{
      this.viewVehicleParams = this.viewVehicleParams.append('despachoDesde', "");
    }

    if(searchCriteria.despachoHasta){
      this.viewVehicleParams = this.viewVehicleParams.append('despachoHasta', searchCriteria.despachoHasta?.toISOString());
    }else{
      this.viewVehicleParams = this.viewVehicleParams.append('despachoHasta', "");
    }

    // @ts-ignore
    this.viewVehicleParams = this.viewVehicleParams.append('estado', searchCriteria.estado);

  }

  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();
    let jsonError="";
    const obs = new Observable((observer: any) => {
      reader.onloadend = (e) => {
        console.log(JSON.parse((<any>e.target).result));
        observer.error(JSON.parse((<any>e.target).result));
        // alert("Error:\n" + (<any>e.target).result);
        observer.complete();
      }
    });
    reader.readAsText(err.error);
    return obs;
  }

  reprintRemitos(impresora: String, remitos: Array<ViewVehicle>): Observable<ViewVehicle[]> {
    return this.httpClient.post<ViewVehicle[]>(this.vehicleRemitosUrl + "/reprint/send/to/" + impresora, remitos).pipe(
      catchError((err) => {
        console.log("Reprint Error", err)
        return throwError(err);
      })
    );
  }

  cancelRemito(cancelRemito: ViewVehicle): Observable<ViewVehicle> {
    return this.httpClient.put<ViewVehicle>(this.vehicleRemitosUrl + '/cancel', cancelRemito);
  }

  sendPrintedVehRemitos(nombre: string | null, impresoraCode: string):  Observable<string> {
    return this.httpClient.put<string>(this.vehicleRemitosUrl + '/send/' + nombre + '/to/' + impresoraCode, null);
  }

  getPrintedVehRemitos(vehRemPrintData: VehRemPrinted): Observable<VehRemPrinted[]> {
    this.vehRemPrintedParams = new HttpParams();
    // @ts-ignore
    this.vehRemPrintedParams = this.vehRemPrintedParams.append('nombre', vehRemPrintData.nombre);
    // @ts-ignore
    this.vehRemPrintedParams = this.vehRemPrintedParams.append('fecha', vehRemPrintData.fecha);

    return this.httpClient.get<VehRemPrinted[]>(this.vehicleRemitosUrl + '/veh_remitos_printed', { params: this.vehRemPrintedParams }).pipe(map(
      allData => {
        return allData.map((allData) => {
          return VehRemPrinted.transformData(allData);
        })
      }),
      catchError(this.handleError));
  }

  handleError(error: HttpErrorResponse) {
    return throwError(error);
  }

}
